import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "@phosphor-icons/react";
import Logo from "@/components/site/Logo";
import Footer from "@/components/site/Footer";
import { COMPANY } from "@/lib/siteData";

const LAST_UPDATED = "December 1, 2025";

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `Privacy Policy | ${COMPANY.name}`;
  }, []);

  return (
    <main
      data-testid="privacy-page"
      className="bg-[#1a1a1a] text-white min-h-screen"
    >
      {/* Simple navbar */}
      <header className="border-b border-white/10 bg-[#121212]/90 backdrop-blur sticky top-0 z-40">
        <div className="max-w-[1100px] mx-auto px-5 md:px-10 py-4 flex items-center justify-between">
          <Logo />
          <Link
            to="/"
            data-testid="privacy-back-home"
            className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.22em] uppercase text-white/75 hover:text-[#c41e3a] transition-colors"
          >
            <ArrowLeft size={14} weight="bold" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative bg-[#121212] grain border-b border-white/10">
        <div className="max-w-[1100px] mx-auto px-5 md:px-10 py-16 md:py-24 relative z-10">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-10 h-[2px] bg-[#c41e3a]" />
            <span className="font-mono-upper text-[11px] text-[#c41e3a]">
              Legal
            </span>
          </div>
          <h1 className="font-display font-black text-white text-4xl md:text-6xl leading-[1] tracking-[-0.03em]">
            Privacy
            <span className="block text-[#c41e3a]">Policy</span>
          </h1>
          <p className="font-body text-white/65 text-base md:text-lg mt-6 max-w-2xl leading-relaxed">
            Your privacy matters to us. This page explains what information we
            collect when you visit our website or contact us, how we use it,
            and the choices you have.
          </p>

          <div className="mt-8 inline-flex items-center gap-3 border border-white/10 px-4 py-2.5">
            <ShieldCheck size={18} weight="duotone" className="text-[#c41e3a]" />
            <span className="font-mono-upper text-[10px] text-white/70">
              Last updated: {LAST_UPDATED}
            </span>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="bg-[#1a1a1a] py-14 md:py-20">
        <div className="max-w-[900px] mx-auto px-5 md:px-10">
          <article className="prose-policy space-y-10">
            {/* 1 */}
            <Block
              n="01"
              title="Who We Are"
              body={
                <>
                  <p>
                    This website is operated by <strong>{COMPANY.name}</strong>{" "}
                    (&quot;we&quot;, &quot;us&quot;, or &quot;our&quot;), a
                    roofing services company based in Saskatoon, Saskatchewan,
                    Canada. We are the data controller responsible for any
                    personal information collected through this website.
                  </p>
                  <p>
                    <strong>Address:</strong> {COMPANY.address}
                    <br />
                    <strong>Phone:</strong> {COMPANY.phone}
                    <br />
                    <strong>Email:</strong>{" "}
                    <a
                      href={`mailto:${COMPANY.email}`}
                      className="text-[#c41e3a] hover:underline"
                    >
                      {COMPANY.email}
                    </a>
                  </p>
                </>
              }
            />

            {/* 2 */}
            <Block
              n="02"
              title="Information We Collect"
              body={
                <>
                  <p>
                    We collect personal information that you voluntarily provide
                    when you contact us by phone, email, or through any form on
                    this website. This may include:
                  </p>
                  <ul>
                    <li>Your name</li>
                    <li>Phone number</li>
                    <li>Email address</li>
                    <li>Postal/street address of the project property</li>
                    <li>
                      Project details, photographs, or any messages you choose
                      to share with us
                    </li>
                  </ul>
                  <p>
                    We also collect non-personal technical information
                    automatically when you browse our site, including IP
                    address, browser type, device type, pages visited, and
                    referring URLs. This is collected through standard server
                    logs and analytics tools.
                  </p>
                </>
              }
            />

            {/* 3 */}
            <Block
              n="03"
              title="How We Use Your Information"
              body={
                <>
                  <p>We use the information we collect to:</p>
                  <ul>
                    <li>Respond to your quote requests, questions, or inquiries</li>
                    <li>Schedule on-site assessments and provide estimates</li>
                    <li>
                      Send service updates, project communications, and follow-ups
                    </li>
                    <li>Improve our website, services, and customer experience</li>
                    <li>
                      Comply with legal obligations, including invoicing,
                      taxation, warranty records, and WCB requirements
                    </li>
                    <li>
                      Deliver and measure advertising on platforms such as Meta
                      (Facebook &amp; Instagram) and Google
                    </li>
                  </ul>
                </>
              }
            />

            {/* 4 */}
            <Block
              n="04"
              title="Cookies & Tracking Technologies"
              body={
                <>
                  <p>
                    Our website uses cookies and similar tracking technologies
                    (such as pixels, tags, and local storage) to operate the
                    site, remember your preferences, and measure traffic. We
                    may use the following categories of cookies:
                  </p>
                  <ul>
                    <li>
                      <strong>Essential cookies</strong> — required for the
                      site to function properly.
                    </li>
                    <li>
                      <strong>Analytics cookies</strong> — help us understand
                      how visitors use our site so we can improve it.
                    </li>
                    <li>
                      <strong>Advertising cookies</strong> — used by partners
                      such as <strong>Meta (Facebook) Pixel</strong> and{" "}
                      <strong>Google Ads</strong> to deliver relevant
                      advertising and measure ad performance.
                    </li>
                  </ul>
                  <p>
                    You can control or disable cookies through your browser
                    settings at any time. Note that disabling certain cookies
                    may affect site functionality.
                  </p>
                </>
              }
            />

            {/* 5 */}
            <Block
              n="05"
              title="Meta (Facebook) & Third-Party Advertising"
              body={
                <>
                  <p>
                    We may run advertising campaigns on Meta platforms
                    (Facebook, Instagram, Messenger) and other third-party
                    advertising networks. In doing so, we may use the{" "}
                    <strong>Meta Pixel</strong> and similar tools to:
                  </p>
                  <ul>
                    <li>Show our ads to people who have visited our website</li>
                    <li>
                      Measure the effectiveness of our advertising campaigns
                    </li>
                    <li>
                      Build look-alike audiences of people similar to our
                      existing customers
                    </li>
                  </ul>
                  <p>
                    Information collected by these tools (such as your IP
                    address, browser, and pages visited) is shared with Meta
                    Platforms, Inc. and processed in accordance with{" "}
                    <a
                      href="https://www.facebook.com/policy.php"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#c41e3a] hover:underline"
                    >
                      Meta&apos;s Data Policy
                    </a>
                    . You may opt out of personalised advertising on Meta
                    platforms in your{" "}
                    <a
                      href="https://www.facebook.com/settings?tab=ads"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#c41e3a] hover:underline"
                    >
                      Facebook ad preferences
                    </a>{" "}
                    or via the{" "}
                    <a
                      href="https://www.youradchoices.ca/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#c41e3a] hover:underline"
                    >
                      Digital Advertising Alliance of Canada
                    </a>
                    .
                  </p>
                </>
              }
            />

            {/* 6 */}
            <Block
              n="06"
              title="How We Share Your Information"
              body={
                <>
                  <p>
                    We do not sell, rent, or trade your personal information.
                    We only share information in the following limited
                    situations:
                  </p>
                  <ul>
                    <li>
                      With trusted service providers who help us operate our
                      business (e.g., email, accounting, scheduling, hosting,
                      analytics, and advertising platforms)
                    </li>
                    <li>
                      With subcontractors or material suppliers when necessary
                      to complete your roofing project
                    </li>
                    <li>
                      When required by law, court order, regulatory authority,
                      or to protect our legal rights
                    </li>
                  </ul>
                </>
              }
            />

            {/* 7 */}
            <Block
              n="07"
              title="Data Retention"
              body={
                <>
                  <p>
                    We keep your personal information only as long as is
                    necessary for the purposes described in this policy or as
                    required by applicable law (e.g., warranty obligations and
                    tax/accounting records, typically up to 7 years). When no
                    longer needed, we securely delete or anonymise it.
                  </p>
                </>
              }
            />

            {/* 8 */}
            <Block
              n="08"
              title="Data Security"
              body={
                <>
                  <p>
                    We take reasonable physical, technical, and administrative
                    measures to safeguard the personal information we hold
                    against loss, theft, unauthorised access, disclosure,
                    copying, use, or modification. However, no method of
                    transmission over the Internet or electronic storage is
                    100% secure, and we cannot guarantee absolute security.
                  </p>
                </>
              }
            />

            {/* 9 */}
            <Block
              n="09"
              title="Your Rights"
              body={
                <>
                  <p>
                    Subject to applicable law (including PIPEDA in Canada, and
                    GDPR/CCPA where relevant), you have the right to:
                  </p>
                  <ul>
                    <li>Access the personal information we hold about you</li>
                    <li>Request correction of inaccurate information</li>
                    <li>Request deletion of your personal information</li>
                    <li>Withdraw consent for any use of your data</li>
                    <li>
                      Opt out of marketing communications at any time by
                      replying &quot;UNSUBSCRIBE&quot; to any email or
                      contacting us directly
                    </li>
                    <li>Lodge a complaint with the relevant data protection authority</li>
                  </ul>
                  <p>
                    To exercise any of these rights, please contact us at{" "}
                    <a
                      href={`mailto:${COMPANY.email}`}
                      className="text-[#c41e3a] hover:underline"
                    >
                      {COMPANY.email}
                    </a>
                    .
                  </p>
                </>
              }
            />

            {/* 10 */}
            <Block
              n="10"
              title="Children's Privacy"
              body={
                <>
                  <p>
                    Our website and services are not directed to children
                    under the age of 13. We do not knowingly collect personal
                    information from children. If you believe a child has
                    provided us with personal information, please contact us
                    so we can delete it.
                  </p>
                </>
              }
            />

            {/* 11 */}
            <Block
              n="11"
              title="Third-Party Links"
              body={
                <>
                  <p>
                    Our website may contain links to third-party websites
                    (such as Google Maps or social media platforms). We are
                    not responsible for the privacy practices of those sites
                    and recommend you review their privacy policies before
                    providing any personal information.
                  </p>
                </>
              }
            />

            {/* 12 */}
            <Block
              n="12"
              title="Changes to This Policy"
              body={
                <>
                  <p>
                    We may update this Privacy Policy from time to time. When
                    we do, we will revise the &quot;Last updated&quot; date at
                    the top of this page. We encourage you to review this
                    policy periodically to stay informed about how we protect
                    your information.
                  </p>
                </>
              }
            />

            {/* 13 */}
            <Block
              n="13"
              title="Contact Us"
              body={
                <>
                  <p>
                    If you have any questions, concerns, or requests regarding
                    this Privacy Policy or how we handle your personal
                    information, please contact us:
                  </p>
                  <ul>
                    <li>
                      <strong>Company:</strong> {COMPANY.name}
                    </li>
                    <li>
                      <strong>Email:</strong>{" "}
                      <a
                        href={`mailto:${COMPANY.email}`}
                        className="text-[#c41e3a] hover:underline"
                      >
                        {COMPANY.email}
                      </a>
                    </li>
                    <li>
                      <strong>Phone:</strong>{" "}
                      <a
                        href={`tel:${COMPANY.phoneRaw}`}
                        className="text-[#c41e3a] hover:underline"
                      >
                        {COMPANY.phone}
                      </a>
                    </li>
                    <li>
                      <strong>Address:</strong> {COMPANY.address}
                    </li>
                  </ul>
                </>
              }
            />
          </article>

          {/* Back to home CTA */}
          <div className="mt-16 flex flex-col sm:flex-row items-start sm:items-center gap-4 border-t border-white/10 pt-10">
            <Link
              to="/"
              data-testid="privacy-bottom-cta"
              className="group inline-flex items-center gap-3 bg-[#c41e3a] hover:bg-[#e62244] text-white font-bold tracking-[0.22em] uppercase text-[11px] px-6 py-4 transition-colors"
            >
              <ArrowLeft
                size={16}
                weight="bold"
                className="transition-transform group-hover:-translate-x-1"
              />
              Back to Home
            </Link>
            <span className="font-mono-upper text-[10px] text-white/40">
              © {new Date().getFullYear()} {COMPANY.name}
            </span>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

const Block = ({ n, title, body }) => (
  <section data-testid={`policy-section-${n}`} className="scroll-mt-24">
    <div className="flex items-start gap-5 mb-5">
      <span className="font-display font-black text-[#c41e3a] text-3xl md:text-4xl leading-none tracking-tighter shrink-0">
        {n}
      </span>
      <div>
        <span className="font-mono-upper text-[10px] text-white/40 block mb-1">
          Section
        </span>
        <h2 className="font-display font-bold text-white text-2xl md:text-3xl tracking-tight leading-tight">
          {title}
        </h2>
      </div>
    </div>
    <div className="pl-0 md:pl-[60px] font-body text-white/75 text-[15px] md:text-base leading-[1.75] space-y-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_strong]:text-white [&_strong]:font-bold">
      {body}
    </div>
  </section>
);

export default PrivacyPolicy;
